import { createSignal, For, onMount, Show, type Component } from "solid-js";
import { PanelLayout, type PanelStatus } from "@/shared/panel-base";
import { useToolClient } from "@/shared/tool-client";
import { registerPanel } from "@/panel-registry";

interface LogSource {
  id: string;
  label?: string;
  type?: string;
  path?: string | null;
  unit?: string | null;
}

interface SourcesResult {
  rows?: LogSource[];
}

interface LogResult {
  sourceId: string;
  label?: string;
  lines: string[];
  count: number;
  fetchedAt?: string;
}

const UbuntuLogsPanel: Component = () => {
  const tools = useToolClient();
  const [status, setStatus] = createSignal<PanelStatus>({ text: "", level: "info" });
  const [sources, setSources] = createSignal<LogSource[]>([]);
  const [sourceId, setSourceId] = createSignal("remotews");
  const [query, setQuery] = createSignal("");
  const [lines, setLines] = createSignal<string[]>([]);
  const [loading, setLoading] = createSignal(false);

  async function loadSources(): Promise<string> {
    try {
      const r = await tools.call<SourcesResult>("log_sources_list", {});
      const rows = Array.isArray(r.rows) ? r.rows : [];
      setSources(rows);
      const selected = rows.length && !rows.some((s) => s.id === sourceId()) ? rows[0].id : sourceId();
      if (selected !== sourceId()) setSourceId(selected);
      return selected;
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), level: "danger" });
      return sourceId();
    }
  }

  async function loadLogs(search: boolean, selectedSourceId: string, searchQuery: string): Promise<void> {
    setLoading(true);
    try {
      const r = await tools.call<LogResult>(search ? "log_search" : "log_tail", {
        sourceId: selectedSourceId,
        lines: 500,
        query: search ? searchQuery : "",
      });
      setLines(r.lines || []);
      setStatus({ text: `${r.count ?? r.lines?.length ?? 0} ligne(s) - ${r.label ?? r.sourceId}`, level: "info" });
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), level: "danger" });
    } finally {
      setLoading(false);
    }
  }

  onMount(() => {
    void loadSources().then((selected) => loadLogs(false, selected, ""));
  });

  const toolbar = (
    <>
      <select class="panel-select" value={sourceId()} onChange={(e) => setSourceId(e.currentTarget.value)}>
        <For each={sources()}>
          {(s) => (
            <option value={s.id}>
              {s.label ?? s.id} {s.type ? `(${s.type})` : ""}
            </option>
          )}
        </For>
      </select>
      <input
        class="panel-filter-input"
        value={query()}
        onInput={(e) => setQuery(e.currentTarget.value)}
        placeholder="Recherche..."
        spellcheck={false}
      />
      <button class="btn btn--sm" onClick={() => void loadLogs(false, sourceId(), "")} disabled={loading()}>
        Tail
      </button>
      <button
        class="btn btn--sm btn--primary"
        onClick={() => void loadLogs(true, sourceId(), query().trim())}
        disabled={loading() || !query().trim()}
      >
        Chercher
      </button>
    </>
  );

  return (
    <PanelLayout title="Logs Ubuntu" toolbar={toolbar} status={status()}>
      <pre class="ubuntu-logs">
        <Show when={lines().length > 0} fallback="Aucun log charge.">
          <For each={lines()}>{(line) => <div>{line}</div>}</For>
        </Show>
      </pre>
      <style>{`
        .ubuntu-logs {
          min-height: 480px;
          max-height: 68vh;
          overflow: auto;
          margin: 0;
          padding: 12px;
          border: 1px solid var(--border, #333);
          border-radius: 6px;
          background: #080a0d;
          color: #d8dee9;
          font: 12px/1.45 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          white-space: pre-wrap;
        }
      `}</style>
    </PanelLayout>
  );
};

registerPanel("ubuntuLogs", UbuntuLogsPanel);

export default UbuntuLogsPanel;
